import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, CreditCard, FileText, Smartphone, Check, Star, QrCode, Copy, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { supabase } from '../../config/supabase';
import { AutocompleteInput } from '../ui/AutocompleteInput';
import SubscriptionSuccessModal from './SubscriptionSuccessModal';
import { trackEvent } from '../../lib/meta-pixel';

interface Plan {
  id: string;
  plan_key: string;
  preapproval_plan_id?: string;
  name: string;
  amount: string; // Mudando para string pois vem como decimal do Supabase
  currency_id: string;
  frequency: number;
  frequency_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  description?: string;
  features?: string[];
}

interface CustomerData {
  name: string;
  email: string;
  cpfCnpj: string;
  mobilePhone: string;
}

interface CreditCardData {
  holderName: string;
  number: string;
  expiryDate: string; // Formato MM/AA
  ccv: string;
  postalCode: string;
  addressNumber: string;
  addressComplement?: string;
}

interface AsaasCheckoutProps {
  planKey?: string;
  variant?: 'default' | 'gold' | 'blue';
}

const AsaasCheckout: React.FC<AsaasCheckoutProps> = ({ planKey, variant = 'default' }) => {
  const { user, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const preSelectedPlanKey = planKey || searchParams.get('plan');
  
  // Estilos baseados na variante
  const isGold = variant === 'gold';
  const isBlue = variant === 'blue';
  
  const buttonClass = isGold 
    ? "w-full py-4 rounded-xl bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 text-black font-black text-lg shadow-[0_0_20px_rgba(255,215,0,0.3)] relative overflow-hidden group border border-yellow-200/50 hover:shadow-[0_0_40px_rgba(255,215,0,0.5)] transition-shadow hover:scale-[1.02] active:scale-[0.98]"
    : isBlue
    ? "w-full py-4 rounded-xl bg-gradient-to-r from-cyan-600 via-cyan-400 to-cyan-600 text-black font-black text-lg shadow-[0_0_20px_rgba(6,182,212,0.3)] relative overflow-hidden group border border-cyan-200/50 hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-shadow hover:scale-[1.02] active:scale-[0.98]"
    : "w-full bg-green-600 hover:bg-green-700 text-white";

  const inputClass = isGold
    ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyber-gold/50 focus:ring-cyber-gold/20"
    : isBlue
    ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-500/50 focus:ring-cyan-500/20"
    : "";

  const labelClass = isGold || isBlue ? "text-gray-300" : "";
  
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingType, setBillingType] = useState<'CREDIT_CARD' | 'PIX'>('CREDIT_CARD');
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [pixData, setPixData] = useState<{ encodedImage: string; payload: string; expirationDate: string } | null>(null);
  
  const [customerData, setCustomerData] = useState<CustomerData>({
    name: '',
    email: '',
    cpfCnpj: '',
    mobilePhone: ''
  });

  const [creditCardData, setCreditCardData] = useState<CreditCardData>({
    holderName: '',
    number: '',
    expiryDate: '',
    ccv: '',
    postalCode: '',
    addressNumber: '',
    addressComplement: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{ planName: string; nextBillingDate?: string; isRenewal?: boolean; licenseKey?: string } | null>(null);
  const [activeSubscription, setActiveSubscription] = useState<any>(null);

  // Efeito para rastrear InitiateCheckout quando um plano é selecionado pela primeira vez
  useEffect(() => {
    if (selectedPlan && plans.length > 0) {
      const plan = plans.find(p => p.id === selectedPlan);
      if (plan) {
        trackEvent('InitiateCheckout', {
          content_name: plan.name,
          content_ids: [plan.id],
          value: parseFloat(plan.amount),
          currency: 'BRL'
        });
      }
    }
  }, [selectedPlan, plans]); // Dispara quando o plano selecionado muda ou planos são carregados

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    
    // Preparar parâmetros para a página de obrigado
    const params = new URLSearchParams();
    if (successData?.planName) params.append('planName', successData.planName);
    
    // Tentar encontrar o plano para pegar o ID/Key
    const plan = plans.find(p => p.id === selectedPlan);
    if (plan?.plan_key) params.append('softwareId', plan.plan_key);
    
    // Se tiver licenseKey, passar também
    if ((successData as any)?.licenseKey) {
        params.append('licenseKey', (successData as any).licenseKey);
    }
    
    window.location.href = `/thank-you?${params.toString()}`;
  };

  // Carregar planos disponíveis
  useEffect(() => {
    loadPlans();
    // Carregar dados salvos do Supabase
    loadSavedCustomerData();
    // Verificar se já tem assinatura ativa
    checkActiveSubscription();
  }, []);

  const checkActiveSubscription = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_licenses')
        .select('plan_key, expires_at')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data && data.plan_key) {
        // Se o plano ativo corresponder ao plano selecionado (se houver) ou for um upgrade
        console.log('User already has active plan:', data.plan_key);
        setActiveSubscription(data);
      }
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error);
    }
  };

  // Pré-preencher dados do usuário logado
  useEffect(() => {
    if (user && userProfile) {
      setCustomerData(prev => ({
        ...prev,
        name: userProfile.display_name || userProfile.full_name || user.email?.split('@')[0] || '',
        email: user.email || '',
        // Manter CPF, celular vazios para o usuário preencher
      }));
    }
  }, [user, userProfile]);

  // Função para salvar CPF e celular no Supabase
  const saveCustomerDataToSupabase = async (cpf: string, mobilePhone: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('users')
        .update({
          cpf: cpf.replace(/\D/g, ''), // Salvar apenas números
          mobile_phone: mobilePhone.replace(/\D/g, '') // Salvar apenas números
        })
        .eq('uid', user.id);

      if (error) {
        console.error('Erro ao salvar dados do cliente:', error);
      }
    } catch (error) {
      console.error('Erro ao salvar dados do cliente:', error);
    }
  };

  // Função para carregar dados salvos do Supabase
  const loadSavedCustomerData = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('cpf, mobile_phone')
        .eq('uid', user.id)
        .single();

      if (error) {
        console.error('Erro ao carregar dados salvos:', error);
        return;
      }

      if (data) {
        setCustomerData(prev => ({
          ...prev,
          cpfCnpj: data.cpf || '',
          mobilePhone: data.mobile_phone || ''
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados salvos:', error);
    }
  };

  const clearSavedFormData = () => {
    try {
      localStorage.removeItem('asaas-checkout-form');
    } catch (error) {
      console.error('Erro ao limpar dados salvos:', error);
    }
  };

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      
      const { data: plans, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('status', 'active')
        .order('amount', { ascending: true });

      if (error) {
        console.error('Erro ao carregar planos:', error);
        toast.error('Erro ao carregar planos');
        return;
      }

      if (plans && plans.length > 0) {
        let mainPlans: Plan[] = [];

        if (preSelectedPlanKey) {
          // Se houver um plano pré-selecionado na URL, filtramos por ele
          // Isso permite checkout de qualquer plano ativo se a key for conhecida
          const targetPlan = plans.find(p => p.plan_key === preSelectedPlanKey);
          if (targetPlan) {
            mainPlans = [targetPlan];
          } else {
             // Fallback se a key não existir: mostrar padrão
             mainPlans = plans.filter(plan => 
              ['explorador', 'criador', 'empresarial_pro'].includes(plan.plan_key)
            );
             toast.error(`Plano "${preSelectedPlanKey}" não encontrado.`);
          }
        } else {
          // Filtrar apenas os planos principais padrão
          mainPlans = plans.filter(plan => 
            ['explorador', 'criador', 'empresarial_pro'].includes(plan.plan_key)
          );
        }

        setPlans(mainPlans);
        if (mainPlans.length > 0) {
          setSelectedPlan(mainPlans[0].id);

          // Track checkout initiation - REMOVIDO DAQUI POIS AGORA ESTÁ NO USEEFFECT
          // O useEffect([selectedPlan]) cuidará disso de forma mais robusta
        }
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      toast.error('Erro ao carregar planos disponíveis');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleCustomerDataChange = (field: keyof CustomerData, value: string) => {
    setCustomerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para lidar com o evento onBlur e salvar dados no Supabase
  const handleCustomerDataBlur = async (field: keyof CustomerData, value: string) => {
    // Atualizar o estado local primeiro
    setCustomerData(prev => ({
      ...prev,
      [field]: value
    }));

    // Salvar no Supabase se for CPF ou celular
    if (field === 'cpfCnpj' || field === 'mobilePhone') {
      const currentData = { ...customerData, [field]: value };
      await saveCustomerDataToSupabase(
        currentData.cpfCnpj || '',
        currentData.mobilePhone || ''
      );
    }
  };

  const handleCreditCardChange = (field: keyof CreditCardData, value: string) => {
    let formattedValue = value;
    
    // Formatação específica para cada campo
    if (field === 'number') {
      formattedValue = value.replace(/\D/g, ''); // Remove tudo que não é dígito
    } else if (field === 'postalCode') {
      formattedValue = value.replace(/\D/g, ''); // Remove tudo que não é dígito
    } else if (field === 'expiryDate') {
      // Formato MM/AA
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 2) {
        formattedValue = numbers;
      } else {
        formattedValue = numbers.slice(0, 2) + '/' + numbers.slice(2, 4);
      }
    } else if (field === 'ccv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4); // Apenas dígitos, máximo 4
    }
    
    setCreditCardData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  // Funções de formatação para exibição
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatCEP = (value: string) => {
    const v = value.replace(/\D/g, '');
    return v.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const formatPhone = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 10) {
      return v.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return v.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const isValidCPF = (cpf: string) => {
    if (typeof cpf !== "string") return false;
    cpf = cpf.replace(/[\s.-]*/igm, '');
    if (
        !cpf ||
        cpf.length != 11 ||
        cpf == "00000000000" ||
        cpf == "11111111111" ||
        cpf == "22222222222" ||
        cpf == "33333333333" ||
        cpf == "44444444444" ||
        cpf == "55555555555" ||
        cpf == "66666666666" ||
        cpf == "77777777777" ||
        cpf == "88888888888" ||
        cpf == "99999999999" 
    ) {
        return false;
    }
    var soma = 0;
    var resto;
    for (var i = 1; i <= 9; i++) 
        soma = soma + parseInt(cpf.substring(i-1, i)) * (11 - i);
    resto = (soma * 10) % 11;
    if ((resto == 10) || (resto == 11))  resto = 0;
    if (resto != parseInt(cpf.substring(9, 10)) ) return false;
    soma = 0;
    for (var i = 1; i <= 10; i++) 
        soma = soma + parseInt(cpf.substring(i-1, i)) * (12 - i);
    resto = (soma * 10) % 11;
    if ((resto == 10) || (resto == 11))  resto = 0;
    if (resto != parseInt(cpf.substring(10, 11) ) ) return false;
    return true;
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    const missingFields: string[] = [];

    // Validar se está logado (o checkout deve usar o email do usuário autenticado)
    const loggedEmail = user?.email?.trim() || '';
    if (!loggedEmail) {
      errors.push('Você precisa estar logado para finalizar o pagamento');
      missingFields.push('Login');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(loggedEmail)) {
        errors.push('Email da conta logada inválido');
        missingFields.push('Email válido da conta');
      }
    }

    // Validar plano selecionado
    if (!selectedPlan) {
      errors.push('Selecione um plano');
      missingFields.push('Plano de assinatura');
    }

    // Validar dados do cliente
    if (!customerData.name.trim()) {
      errors.push('Nome é obrigatório');
      missingFields.push('Nome completo');
    }

    // Validar CPF
    const cpfNumbers = customerData.cpfCnpj.replace(/\D/g, '');
    if (!cpfNumbers || cpfNumbers.length !== 11) {
      errors.push('CPF é obrigatório');
      missingFields.push('CPF válido (11 dígitos)');
    } else if (!isValidCPF(cpfNumbers)) {
      errors.push('CPF inválido');
      missingFields.push('CPF válido (número incorreto)');
    }

    // Validar celular
    const mobileNumbers = customerData.mobilePhone.replace(/\D/g, '');
    if (!mobileNumbers || mobileNumbers.length < 11) {
      errors.push('Celular é obrigatório');
      missingFields.push('Celular válido (com DDD)');
    }

    // Validar dados do cartão de crédito APENAS se for o método selecionado
    if (billingType === 'CREDIT_CARD') {
      // Nome do portador
      if (!creditCardData.holderName.trim()) {
        errors.push('Nome do portador é obrigatório');
        missingFields.push('Nome do portador do cartão');
      }

      // Número do cartão
      const cardNumbers = creditCardData.number.replace(/\D/g, '');
      if (!cardNumbers || cardNumbers.length < 13) {
        errors.push('Número do cartão inválido');
        missingFields.push('Número do cartão válido');
      }

      // Data de expiração MM/AA
      if (!creditCardData.expiryDate || creditCardData.expiryDate.length < 5) {
        errors.push('Data de expiração inválida');
        missingFields.push('Data de expiração válida (MM/AA)');
      } else {
        const [month, year] = creditCardData.expiryDate.split('/');
        const currentYear = new Date().getFullYear() % 100;
        const currentMonth = new Date().getMonth() + 1;
        
        if (!month || parseInt(month) < 1 || parseInt(month) > 12) {
          errors.push('Mês de expiração inválido');
          missingFields.push('Mês de expiração válido');
        }
        
        if (!year || parseInt(year) < currentYear || (parseInt(year) === currentYear && parseInt(month) < currentMonth)) {
          errors.push('Data de expiração inválida');
          missingFields.push('Data de expiração válida');
        }
      }

      // CCV
      if (!creditCardData.ccv || creditCardData.ccv.length < 3) {
        errors.push('CCV é obrigatório');
        missingFields.push('Código de segurança (CCV)');
      }

      // CEP
      if (!creditCardData.postalCode || creditCardData.postalCode.length < 8) {
        errors.push('CEP inválido');
        missingFields.push('CEP válido');
      }

      // Número do endereço
      if (!creditCardData.addressNumber.trim()) {
        errors.push('Número do endereço é obrigatório');
        missingFields.push('Número do endereço');
      }
    }

    // Se há erros, mostrar feedback detalhado
    if (errors.length > 0) {
      if (missingFields.length === 1) {
        toast.error(`❌ Campo obrigatório`, {
          description: `Preencha: ${missingFields[0]}`
        });
      } else if (missingFields.length <= 3) {
        toast.error(`❌ ${missingFields.length} campos obrigatórios`, {
          description: `Preencha: ${missingFields.join(', ')}`
        });
      } else {
        toast.error(`❌ ${missingFields.length} campos obrigatórios`, {
          description: `Preencha: ${missingFields.slice(0, 3).join(', ')} e mais ${missingFields.length - 3} campos`
        });
      }
      return false;
    }

    return true;
  };

  // Efeito para polling de status de pagamento PIX
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let isActive = true;

    // Função de teste para simular compra aprovada (apenas dev)
    // @ts-ignore
    window.simularCompraAprovada = () => {
        const plan = plans.find(p => p.id === selectedPlan);
        if (plan) {
            console.log('🧪 Simulando compra aprovada para:', plan.name);
            trackEvent('Purchase', {
                content_name: plan.name,
                content_ids: [plan.id],
                value: parseFloat(plan.amount),
                currency: 'BRL'
            });
            toast.success('🧪 Teste: Evento Purchase disparado!');
            setSuccessData({
                planName: plan.name,
                nextBillingDate: new Date().toISOString(),
                isRenewal: false
            });
            setShowSuccessModal(true);
        }
    };

    const startPolling = async () => {
      if (billingType === 'PIX' && pixData && user && plans.length > 0) {
        console.log('🔄 Iniciando configuração do polling...');
        
        const selectedPlanObj = plans.find(p => p.id === selectedPlan);
        if (!selectedPlanObj) return;

        // VERIFICAR SE É KEYAUTH (MELHORADOR) - Exceto Melhorador Cloud que usa user_licenses
        const isKeyAuth = (selectedPlanObj.plan_key.includes('melhorador') || 
                          selectedPlanObj.name.toLowerCase().includes('melhorador')) &&
                          !selectedPlanObj.plan_key.includes('cloud') &&
                          !selectedPlanObj.name.toLowerCase().includes('cloud');

        // 1. Obter estado ATUAL da licença (Baseline) antes de iniciar o polling
        // Isso previne falsos positivos se o usuário já tiver uma licença ativa
        let baselineDate: Date = new Date();
        
        if (!isKeyAuth) {
          const { data: currentLicense } = await supabase
            .from('user_licenses')
            .select('expires_at')
            .eq('user_id', user.id)
            .eq('plan_key', selectedPlanObj.plan_key)
            .maybeSingle();

          if (currentLicense?.expires_at) {
            const licenseDate = new Date(currentLicense.expires_at);
            baselineDate = licenseDate > new Date() ? licenseDate : new Date();
          }
        }

        console.log('📅 Baseline para detecção:', baselineDate.toISOString(), isKeyAuth ? '(KeyAuth)' : '(Standard)');

        if (!isActive) return;

        intervalId = setInterval(async () => {
          try {
            if (isKeyAuth) {
               // POLLING PARA KEYAUTH (Tabela license_purchases)
               // Procuramos uma compra recente (criada APÓS o inicio do polling)
               // Como o baselineDate é "agora", procuramos created_at > baselineDate
               const { data, error } = await supabase
                  .from('license_purchases')
                  .select('*')
                  .eq('user_uid', user.id)
                  .eq('product', 'melhorador') // Fixo pois só usamos KeyAuth para isso por enquanto
                  .gt('created_at', baselineDate.toISOString())
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();

               if (data) {
                  console.log('✅ Licença KeyAuth detectada!', data);
                  clearInterval(intervalId);
                  setPixData(null);
                  setSuccessData({
                    planName: selectedPlanObj.name,
                    nextBillingDate: new Date(Date.now() + (data.duration_days || 365) * 86400000).toISOString(),
                    isRenewal: false, // KeyAuth geralmente é nova chave
                    licenseKey: data.license_key // Passamos a chave
                  });
                  setShowSuccessModal(true);
               }

            } else {
              // POLLING PADRÃO (Tabela user_licenses)
              const { data, error } = await supabase
                .from('user_licenses')
                .select('plan_key, expires_at')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .eq('plan_key', selectedPlanObj.plan_key)
                .gt('expires_at', baselineDate.toISOString())
                .limit(1)
                .maybeSingle();

              if (data) {
                console.log('✅ Pagamento confirmado! Nova validade:', data.expires_at);
                
                clearInterval(intervalId);
                setPixData(null);

                const planName = selectedPlanObj.name;
                const isRenewal = baselineDate > new Date();

                setSuccessData({
                  planName: planName,
                  nextBillingDate: data.expires_at,
                  isRenewal: isRenewal
                });
                
                // Track Purchase (PIX success via polling)
                trackEvent('Purchase', {
                  content_name: planName,
                  content_ids: [selectedPlanObj.id],
                  value: parseFloat(selectedPlanObj.amount),
                  currency: 'BRL'
                });

                setShowSuccessModal(true);
              }
            }
          } catch (err) {
            console.error('Erro no polling:', err);
          }
        }, 3000); // Verificar a cada 3 segundos
      }
    };

    startPolling();

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [billingType, pixData, user, plans, selectedPlan]);

  const handleCheckout = async () => {
    if (!validateForm()) return;
    if (loading) return; // Proteção extra contra duplo clique

    // Track AddPaymentInfo (User click pay or generate pix)
    trackEvent('AddPaymentInfo', {
      payment_type: billingType === 'PIX' ? 'PIX' : 'CREDIT_CARD',
      currency: 'BRL',
      value: plans.find(p => p.id === selectedPlan)?.amount
    });

    try {
      setLoading(true);

      // Obter IP do cliente
      const getClientIP = async () => {
        try {
          const response = await fetch('https://api.ipify.org?format=json');
          const data = await response.json();
          return data.ip;
        } catch (error) {
          console.warn('Erro ao obter IP:', error);
          return '127.0.0.1'; // Fallback
        }
      };

      const remoteIp = await getClientIP();

      const checkoutData = {
        planId: selectedPlan,
        userId: user?.id, // Passar o ID do usuário logado
        customerData: {
          ...customerData,
          email: user?.email || customerData.email,
          cpfCnpj: customerData.cpfCnpj.replace(/\D/g, ''),
          mobilePhone: customerData.mobilePhone.replace(/\D/g, '')
        },
        billingType,
        remoteIp, // Adicionar IP do cliente
        ...(billingType === 'CREDIT_CARD' && {
          creditCard: {
            holderName: creditCardData.holderName,
            number: creditCardData.number.replace(/\D/g, ''),
            expiryMonth: creditCardData.expiryDate.split('/')[0]?.padStart(2, '0') || '',
            expiryYear: '20' + (creditCardData.expiryDate.split('/')[1] || ''),
            ccv: creditCardData.ccv
          },
          creditCardHolderInfo: {
            name: customerData.name,
            email: user?.email || customerData.email,
            cpfCnpj: customerData.cpfCnpj.replace(/\D/g, ''),
            postalCode: creditCardData.postalCode.replace(/\D/g, ''),
            addressNumber: creditCardData.addressNumber,
            addressComplement: creditCardData.addressComplement || null,
            phone: customerData.mobilePhone.replace(/\D/g, ''),
            mobilePhone: customerData.mobilePhone.replace(/\D/g, '')
          }
        })
      };

      // Usar edge function do Supabase em vez de cloud function do Firebase
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/asaas-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(checkoutData)
      });

      const result = await response.json();

      if (result.success) {
        // Limpar dados salvos após sucesso
        clearSavedFormData();

        // Se for PIX, mostrar QR Code
        if (billingType === 'PIX' && result.pixQrCode) {
          setPixData(result.pixQrCode);
          toast.success('🎉 Cobrança PIX gerada!', {
            description: 'Escaneie o QR Code ou copie o código para pagar.'
          });
          setLoading(false); // Parar loading para mostrar QR Code
          return;
        }

        // Mostrar feedback específico baseado no status do pagamento
        if (billingType === 'CREDIT_CARD') {
          const status = result.payment?.status;
          console.log('💳 Status do pagamento:', status);

          if (status === 'CONFIRMED' || status === 'RECEIVED') {
            const plan = plans.find(p => p.id === selectedPlan);
            const isRenewal = !!activeSubscription && activeSubscription.plan_key === plan?.plan_key;

            setSuccessData({
              planName: plan?.name || 'Plano Premium',
              nextBillingDate: undefined, // O modal calculará uma data estimada
              isRenewal: isRenewal
            });

            // Track Purchase (Credit Card success - Instant)
            if (plan) {
              trackEvent('Purchase', {
                content_name: plan.name,
                content_ids: [plan.id],
                value: parseFloat(plan.amount),
                currency: 'BRL'
              });
            }

            setShowSuccessModal(true);
            
          } else if (status === 'PENDING' || status === 'AWAITING_RISK_ANALYSIS') {
            toast.info('⏳ Pagamento em processamento', {
              description: 'Aguardando confirmação do cartão de crédito. Verificando status...'
            });
            
            // Iniciar polling para verificar status (opcional, pode só redirecionar)
            setTimeout(() => {
              window.location.href = '/subscription/success';
            }, 3000);
            
          } else if (status) {
            // Para AWAITING_PAYMENT ou outros status de sucesso inicial
            const plan = plans.find(p => p.id === selectedPlan);
            const isRenewal = !!activeSubscription && activeSubscription.plan_key === plan?.plan_key;

            setSuccessData({
              planName: plan?.name || 'Plano Premium',
              nextBillingDate: undefined,
              isRenewal: isRenewal
            });

            // Track Purchase (Credit Card success)
            if (plan) {
              trackEvent('Purchase', {
                content_name: plan.name,
                content_ids: [plan.id],
                value: parseFloat(plan.amount),
                currency: 'BRL'
              });
            }

            setShowSuccessModal(true);
          } else {
            // Fallback se não vier status (assinatura criada mas status do pagamento desconhecido)
          console.warn('⚠️ Status do pagamento não retornado, assumindo sucesso na criação da assinatura.');
          const plan = plans.find(p => p.id === selectedPlan);
          setSuccessData({
            planName: plan?.name || 'Plano Premium',
            nextBillingDate: undefined,
            isRenewal: !!activeSubscription
          });

          // Track Purchase (Fallback success)
          if (plan) {
            trackEvent('Purchase', {
              content_name: plan.name,
              content_ids: [plan.id],
              value: parseFloat(plan.amount),
              currency: 'BRL'
            });
          }

          setShowSuccessModal(true);
        }
      }

        // Mostrar resumo da assinatura criada
        const plan = plans.find(p => p.id === selectedPlan);
        console.log('✅ Checkout concluído:', {
          subscription: result.subscription,
          payment: result.payment,
          plan: plan?.name
        });
        
      } else {
        // Tratamento de erros mais específico e detalhado
        console.error('❌ Erro detalhado do checkout:', result);
        console.log('🔍 Detalhes do erro:', JSON.stringify(result.details, null, 2));
        
        const errorMessage = result.error || 'Erro desconhecido no pagamento';
        
        // Tentar extrair detalhes de diferentes formatos possíveis
        let errorDetails = result.details;
        
        // Se details tiver uma propriedade 'errors' (padrão Asaas), use ela
        if (errorDetails && errorDetails.errors && Array.isArray(errorDetails.errors)) {
          errorDetails = errorDetails.errors;
        }

        // Erros específicos do Asaas
        if (errorDetails && Array.isArray(errorDetails)) {
          const detailsText = errorDetails.map((err: any) => err.description || err.message).join(', ');
          
          console.log('🚨 Mensagem de erro formatada:', detailsText);

          // Verificar tipos de erro comuns
          if (detailsText.includes('cartão') || detailsText.includes('card')) {
            toast.error('❌ Erro no cartão de crédito', {
              description: detailsText
            });
          } else if (detailsText.includes('saldo') || detailsText.includes('insufficient')) {
            toast.error('❌ Saldo insuficiente', {
              description: 'Cartão sem saldo suficiente para a transação'
            });
          } else if (detailsText.includes('negado') || detailsText.includes('denied')) {
            toast.error('❌ Pagamento negado', {
              description: 'Transação negada pelo banco emissor'
            });
          } else if (detailsText.includes('CPF') || detailsText.includes('CNPJ')) {
             toast.error('❌ CPF Inválido', {
              description: 'O Asaas rejeitou o CPF informado. Verifique se está correto.'
            });
          } else if (detailsText.includes('mobilePhone') || detailsText.includes('celular')) {
             toast.error('❌ Celular Inválido', {
              description: 'O formato do celular está incorreto. Use (DD) 9XXXX-XXXX.'
            });
          } else {
            toast.error(`❌ Erro: ${errorMessage}`, {
              description: detailsText
            });
          }
        } else if (errorDetails && typeof errorDetails === 'string') {
          toast.error(`❌ ${errorMessage}`, {
            description: errorDetails
          });
        } else {
          // Mensagens de erro genéricas mais amigáveis
          if (errorMessage.includes('validation')) {
            toast.error('❌ Dados inválidos', {
              description: 'Verifique se todos os campos estão preenchidos corretamente.'
            });
          } else {
            toast.error('❌ Erro no pagamento', {
              description: errorMessage
            });
          }
        }
      }
    } catch (error) {
      console.error('Erro no checkout:', error);
      toast.error('❌ Erro de conexão', {
        description: 'Não foi possível processar o pagamento. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (pixData?.payload) {
      navigator.clipboard.writeText(pixData.payload);
      toast.success('Código PIX copiado!');
    }
  };

  const getPlanPrice = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return 'R$ 0,00';
    
    let amount = parseFloat(plan.amount);
    
    // Aplicar 10% de desconto se for cartão
    if (billingType === 'CREDIT_CARD') {
      amount = amount * 0.9;
    }
    
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
  };
  
  const getPlanOriginalPrice = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return null;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(plan.amount));
  };

  // Se já tiver gerado PIX, mostrar tela de pagamento PIX
  if (pixData) {
    return (
            <Card className={`w-full max-w-md mx-auto text-white ${isGold ? 'bg-black/50 border-cyber-gold/30' : isBlue ? 'bg-black/50 border-cyber-blue/30' : 'bg-gray-900 border-gray-800'}`}>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2 text-2xl">
                  <QrCode className={`w-8 h-8 ${isGold ? 'text-cyber-gold' : isBlue ? 'text-cyber-blue' : 'text-green-500'}`} />
                  Pagamento via PIX
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Escaneie o QR Code ou copie o código abaixo para pagar
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-center p-4 bg-white rounded-lg w-fit mx-auto">
                  <img 
                    src={`data:image/png;base64,${pixData.encodedImage}`} 
                    alt="QR Code PIX" 
                    className="w-48 h-48"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-center block text-gray-400">Código PIX Copia e Cola</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={pixData.payload} 
                      readOnly 
                      className={isGold ? "bg-black/50 border-cyber-gold/30 text-cyber-gold font-mono text-xs" : isBlue ? "bg-black/50 border-cyber-blue/30 text-cyber-blue font-mono text-xs" : "bg-gray-800 border-gray-700 text-gray-300 font-mono text-xs"}
                    />
                    <Button onClick={copyPixCode} variant="secondary" className={isGold ? "bg-cyber-gold/20 text-cyber-gold hover:bg-cyber-gold/30" : isBlue ? "bg-cyber-blue/20 text-cyber-blue hover:bg-cyber-blue/30" : "bg-gray-700 hover:bg-gray-600"}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className={`text-center text-sm p-4 rounded-lg border ${isGold ? 'text-cyber-gold bg-cyber-gold/10 border-cyber-gold/20' : isBlue ? 'text-cyber-blue bg-cyber-blue/10 border-cyber-blue/20' : 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20'}`}>
                  <p>Após o pagamento, sua assinatura será liberada automaticamente em alguns instantes.</p>
                </div>
                
                <Button 
                  className={`w-full mt-4 ${isGold || isBlue ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-800 hover:bg-gray-700'}`}
                  onClick={() => window.location.reload()}
                >
                  Voltar / Novo Pagamento
                </Button>
              </CardContent>
            </Card>
          );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Resumo do Plano */}
      <Card className={isGold ? "bg-transparent border-white/10 shadow-none" : "bg-gray-900 border-gray-800"}>
        <CardHeader>
          <CardTitle className="text-white">Resumo do Pedido</CardTitle>
        </CardHeader>
        <CardContent>
                <div className={`p-4 rounded-xl border ${isGold ? 'bg-cyber-gold/10 border-cyber-gold/30' : isBlue ? 'bg-cyber-blue/10 border-cyber-blue/30' : 'bg-gray-800 border-gray-700'}`}>
                  <h4 className={`text-sm font-medium mb-3 ${isGold ? 'text-cyber-gold' : isBlue ? 'text-cyber-blue' : 'text-gray-400'}`}>Resumo do Pedido</h4>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300 text-sm">{plans.find(p => p.id === selectedPlan)?.name || 'Plano Selecionado'}</span>
                    <div className="text-right">
                       {billingType === 'CREDIT_CARD' && <span className="text-xs line-through text-gray-500 block">{getPlanOriginalPrice()}</span>}
                       <span className={`text-xl font-bold ${isGold ? 'text-cyber-gold' : isBlue ? 'text-cyber-blue' : 'text-white'}`}>{getPlanPrice()}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Cobrança {plans.find(p => p.id === selectedPlan)?.frequency === 1 ? 'Mensal' : 'Anual'}</span>
                    {billingType === 'CREDIT_CARD' && <span className="text-green-500 font-bold">10% DE DESCONTO APLICADO</span>}
                  </div>
                </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Dados do Cliente */}
        <div className="space-y-6">
          <Card className={`h-full ${isGold ? "bg-transparent border-white/10 shadow-none" : "bg-gray-900 border-gray-800"}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Check className={`w-5 h-5 ${isGold ? 'text-cyber-gold' : 'text-purple-500'}`} />
                Seus Dados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className={labelClass || "text-gray-300"}>Nome Completo</Label>
                <Input 
                  id="name" 
                  value={customerData.name} 
                  onChange={(e) => handleCustomerDataChange('name', e.target.value)}
                  placeholder="Seu nome completo"
                  className={inputClass || "bg-gray-800 border-gray-700 text-white focus:ring-purple-500"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className={labelClass || "text-gray-300"}>Email da conta logada</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={customerData.email} 
                  disabled
                  placeholder="Email da conta logada"
                  className={inputClass || "bg-gray-800 border-gray-700 text-white focus:ring-purple-500"}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cpf" className={labelClass || "text-gray-300"}>CPF</Label>
                  <Input 
                    id="cpf" 
                    value={formatCPF(customerData.cpfCnpj)} 
                    onChange={(e) => handleCustomerDataChange('cpfCnpj', e.target.value)}
                    onBlur={(e) => handleCustomerDataBlur('cpfCnpj', e.target.value)}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className={inputClass || "bg-gray-800 border-gray-700 text-white focus:ring-purple-500"}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className={labelClass || "text-gray-300"}>Celular</Label>
                  <Input 
                    id="phone" 
                    value={formatPhone(customerData.mobilePhone)} 
                    onChange={(e) => handleCustomerDataChange('mobilePhone', e.target.value)}
                    onBlur={(e) => handleCustomerDataBlur('mobilePhone', e.target.value)}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                    className={inputClass || "bg-gray-800 border-gray-700 text-white focus:ring-purple-500"}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pagamento */}
        <div className="space-y-6">
          <Card className={`h-full ${isGold ? "bg-transparent border-white/10 shadow-none" : "bg-gray-900 border-gray-800"}`}>
            <CardHeader>
              <CardTitle className="text-white">Forma de Pagamento</CardTitle>
              <CardDescription className="text-gray-400">Escolha como deseja pagar sua assinatura</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <RadioGroup 
                className="grid grid-cols-2 gap-4"
              >
                <div onClick={() => setBillingType('CREDIT_CARD')} className="cursor-pointer">
                  <RadioGroupItem value="CREDIT_CARD" id="card" className="peer sr-only" />
                  <Label
                    htmlFor="card"
                    className={`flex flex-col items-center justify-between rounded-xl border-2 p-4 transition-all h-full cursor-pointer ${
                      billingType === 'CREDIT_CARD' 
                        ? isGold ? 'border-cyber-gold bg-cyber-gold/10 text-white shadow-[0_0_20px_rgba(255,215,0,0.2)]' : 'border-purple-500 bg-purple-500/10 text-white shadow-[0_0_20px_rgba(168,85,247,0.2)]' 
                        : isGold ? 'border-white/10 bg-white/5 hover:bg-white/10 text-gray-400' : 'border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-400'
                    }`}
                  >
                    <CreditCard className={`mb-2 h-8 w-8 ${billingType === 'CREDIT_CARD' ? (isGold ? 'text-cyber-gold' : 'text-purple-400') : 'text-gray-500'}`} />
                    <span className="font-bold text-lg">Cartão</span>
                    <span className={`text-xs mt-2 font-bold px-2 py-1 rounded-full flex items-center gap-1 ${
                      billingType === 'CREDIT_CARD' 
                        ? isGold ? 'bg-cyber-gold text-black' : 'bg-green-500 text-black' 
                        : isGold ? 'bg-white/10 text-gray-400' : 'bg-green-900/30 text-green-500'
                    }`}>
                      <Percent className="w-3 h-3" /> 10% OFF
                    </span>
                  </Label>
                </div>
                
                <div onClick={() => setBillingType('PIX')} className="cursor-pointer">
                  <RadioGroupItem value="PIX" id="pix" className="peer sr-only" />
                  <Label
                    htmlFor="pix"
                    className={`flex flex-col items-center justify-between rounded-xl border-2 p-4 transition-all h-full cursor-pointer ${
                      billingType === 'PIX' 
                        ? isGold ? 'border-cyber-gold bg-cyber-gold/10 text-white shadow-[0_0_20px_rgba(255,215,0,0.2)]' : 'border-green-500 bg-green-500/10 text-white shadow-[0_0_20px_rgba(34,197,94,0.2)]' 
                        : isGold ? 'border-white/10 bg-white/5 hover:bg-white/10 text-gray-400' : 'border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <QrCode className={`h-8 w-8 ${billingType === 'PIX' ? (isGold ? 'text-cyber-gold' : 'text-green-400') : 'text-gray-500'}`} />
                    </div>
                    <span className="font-bold text-lg">PIX</span>
                    <span className={`text-xs mt-2 font-bold px-2 py-1 rounded-full ${
                      billingType === 'PIX'
                        ? isGold ? 'bg-cyber-gold/20 text-cyber-gold' : 'bg-green-500/20 text-green-400'
                        : isGold ? 'bg-white/10 text-gray-400' : 'bg-gray-700 text-gray-500'
                    }`}>
                      Aprovação Imediata
                    </span>
                  </Label>
                </div>
              </RadioGroup>

              {billingType === 'CREDIT_CARD' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                  <div className="space-y-2">
                    <Label htmlFor="cardName" className={labelClass || "text-gray-300"}>Nome no Cartão</Label>
                    <Input 
                      id="cardName" 
                      value={creditCardData.holderName} 
                      onChange={(e) => handleCreditCardChange('holderName', e.target.value)}
                      placeholder="Nome como está no cartão"
                      className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cardNumber" className={labelClass || "text-gray-300"}>Número do Cartão</Label>
                      <Input 
                        id="cardNumber" 
                        value={formatCardNumber(creditCardData.number)} 
                        onChange={(e) => handleCreditCardChange('number', e.target.value)}
                        placeholder="0000 0000 0000 0000"
                        maxLength={19}
                        className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cardExpiry" className={labelClass || "text-gray-300"}>Validade</Label>
                      <Input 
                        id="cardExpiry" 
                        value={creditCardData.expiryDate} 
                        onChange={(e) => handleCreditCardChange('expiryDate', e.target.value)}
                        placeholder="MM/AA"
                        maxLength={5}
                        className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cardCcv" className={labelClass || "text-gray-300"}>CVV</Label>
                      <Input 
                        id="cardCcv" 
                        value={creditCardData.ccv} 
                        onChange={(e) => handleCreditCardChange('ccv', e.target.value)}
                        placeholder="123"
                        maxLength={4}
                        className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cardCep" className={labelClass || "text-gray-300"}>CEP</Label>
                      <Input 
                        id="cardCep" 
                        value={formatCEP(creditCardData.postalCode)} 
                        onChange={(e) => handleCreditCardChange('postalCode', e.target.value)}
                        placeholder="00000-000"
                        maxLength={9}
                        className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cardAddressNum" className={labelClass || "text-gray-300"}>Número do Endereço</Label>
                    <Input 
                      id="cardAddressNum" 
                      value={creditCardData.addressNumber} 
                      onChange={(e) => handleCreditCardChange('addressNumber', e.target.value)}
                      placeholder="123"
                      className={inputClass || "bg-gray-800 border-gray-700 text-white"}
                    />
                  </div>
                </div>
              )}

              {billingType === 'PIX' && (
                <div className={`p-4 rounded-lg border text-center animate-in fade-in slide-in-from-top-4 duration-300 ${isGold ? 'bg-white/5 border-white/10' : 'bg-gray-800/50 border-gray-700'}`}>
                  <p className="text-gray-300 mb-2">
                    Ao confirmar, um <strong>QR Code PIX</strong> será gerado.
                  </p>
                  <p className="text-sm text-gray-500">
                    Você poderá pagar usando o aplicativo do seu banco. A liberação é automática após o pagamento.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Button 
        className={isGold || isBlue
          ? buttonClass
          : `w-full py-6 text-lg font-bold shadow-lg transition-all hover:scale-[1.01] ${
            billingType === 'CREDIT_CARD' 
              ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-purple-900/20' 
              : 'bg-green-600 hover:bg-green-700 text-white shadow-green-900/20'
          }`
        }
        onClick={handleCheckout}
        disabled={loading || loadingPlans}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processando...
          </>
        ) : (
          <>
            {isGold || isBlue ? (
              <span className="relative z-10 flex items-center justify-center gap-2">
                {billingType === 'CREDIT_CARD' ? 'FINALIZAR ASSINATURA' : 'GERAR PIX AGORA'} 
                {isGold && <Star className="w-4 h-4 fill-black" />}
                {isBlue && <Check className="w-4 h-4" />}
              </span>
            ) : (
              billingType === 'CREDIT_CARD' ? 'Finalizar Pagamento com Cartão' : 'Gerar PIX para Pagamento'
            )}
            {(isGold || isBlue) && (
              <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-12 group-hover:animate-shine" />
            )}
          </>
        )}
      </Button>

      <div className="flex justify-center items-center gap-4 text-gray-500 text-sm">
        <div className="flex items-center gap-1">
          <Check className="w-4 h-4" /> Pagamento Seguro
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4" /> Garantia de 7 dias
        </div>
      </div>

      {/* Modal integration */}
      <SubscriptionSuccessModal 
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        planName={successData?.planName || 'Plano Premium'}
        nextBillingDate={successData?.nextBillingDate}
        isRenewal={successData?.isRenewal}
        licenseKey={(successData as any)?.licenseKey}
      />
    </div>
  );
};

export default AsaasCheckout;
